// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@gcg/App/Components/Notify";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button/Button";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface NavigationPromotionStyles {
    container?: InjectableCss;
    header?: InjectableCss;
    button?: InjectableCss;
    content?: InjectableCss;
    image?: InjectableCss;
}
export const navigationPromotionStyles: NavigationPromotionStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            padding-left: 15px;
            width: 100%;
        `,
    },
    header: {
        css: css`
            display: flex;
            justify-content: space-between;
            h2 {
                font-size: 22px;
                font-weight: bold;
                margin-bottom: 10px;
            }
        `,
    },
    button: {
        css: css``,
    },
    content: {
        css: css`
            * {
                font-size: 14px;
                color: ${getColor("text.main")};
            }
        `,
    },
    image: {
        css: css`
            overflow: hidden;
        `,
    },
};

const style = navigationPromotionStyles;

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getPageStateByType(state, "AllProducts"),
    currentLanguage: state.context.session.language,
    state,
});

const mapDispatchToProps = {
    loadPageByType,
};

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const getPromotionPageById = (state: ApplicationState, id: string) => {
    return getPageLinkByNodeId(state, id);
};

const NavigationPromotion: React.FC<OwnProps> = ({ pageType, loadPageByType, currentLanguage, state }) => {
    const [pageValue, setPageValue] = React.useState({} as any);
    const [promotionPage, setPromotionPage] = React.useState({} as any);
    const [pageTraget, setPromotionPageTarget] = React.useState(false);

    React.useEffect(() => {
        if (!pageType || !pageType.value) {
            loadPageByType("AllProducts");
        }
    }, []);

    React.useEffect(() => {
        setPageValue(pageType?.value);
        // eslint-disable-next-line no-prototype-builtins
        if (pageValue && pageValue.hasOwnProperty("generalFields")) {
            const promotionPage = getPromotionPageById(state, pageValue?.generalFields["link"].value);
            setPromotionPage(promotionPage);
            setPromotionPageTarget(pageValue?.generalFields["openInNewTab"]);
        }
    }, [pageType]);

    if (!pageValue) {
        return <Notify messageType="danger" body={siteMessage("Custom_AllProductsPage_NotFound")} />;
    }

    return (
        <>
            {pageValue && pageValue.generalFields && (
                <StyledWrapper {...style.container}>
                    <StyledWrapper {...style.header}>
                        <Typography as="h2" color="text.main" line-height="20">
                            {translate(pageValue.translatableFields["header"][currentLanguage!.id].toString())} 							
							
                        </Typography>

                        <StyledWrapper>
                            <Button
                                {...({ as: "a" } as any)}
                                sizeVariant="small"
                                target={pageTraget ? "_blank" : "_self"}
                                href={promotionPage?.url}
                            >
                                {translate("Shop All")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...style.content}>
                        <Typography>
                            {parse(pageValue.translatableFields["copy"][currentLanguage!.id].toString(), parserOptions)}
                        </Typography>
                    </StyledWrapper>

                    <StyledWrapper {...style.image}>
                        <LazyImage src={pageValue?.generalFields["imageUrl"]} altText={translate("Promotion Image")} />
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationPromotion);
